<template>
    <div class="dev-tools-wrapper">
        <div id="dev-tools">
            <div class="far fa-code"></div>
        </div>
        <elem-dropdown triggerSelector="#dev-tools" position="right-end" :offset="[-35, 0]" :closeOnClick="false">
            <div class="dropdown">
                <h5>Dev Tools</h5>
                <div class="header">Links</div>
                <router-link to="/apitest">API Test</router-link>
                <router-link to="/styleguide">Style Guide</router-link>
                <div class="header">API Endpoint</div>
                <div class="item short">
                    <form-input :value="apiEndpoint" @input="updateApiEndpoint" paddingTop="0" />
                    <a
                        class="end-button"
                        :class="{ invisible: !endpointChanged }"
                        @click="resetApiEndpoint"
                        title="Reset API endpoint"
                    >
                        <i class="far fa-undo-alt"></i>
                    </a>
                </div>
                <div :class="{ disabled: !useQAStatusEndpoint && !$env.APP_STATUS_URL }">
                    <div class="header app-status">
                        App Status
                        <i class="extra-info far fa-question-circle" @click="showAppStatusInfo" />
                    </div>
                    <div class="item override">
                        <form-toggle
                            label="Use QA status URL"
                            :checked="useQAStatusEndpoint"
                            @change="toggleUseQAStatusEndpoint"
                            layout="horizontal"
                            size="small"
                        />
                    </div>
                    <div class="item">
                        <button
                            class="primary small refresh"
                            title="If the frontend was loaded before APP_EXPIRE, it will refresh when convenient."
                            @click="() => updateStatus('APP_EXPIRE', Date.now())"
                        >
                            Force App<br />Expire
                        </button>
                        <div class="time">App loaded {{ appLoadedTime }}</div>
                        <div class="time">App expire {{ appExpireTime }}</div>
                    </div>
                    <div class="item">
                        <form-toggle
                            label="Maintenance Mode"
                            :checked="appStatus.MAINTENANCE_MODE"
                            @change="updateStatus('MAINTENANCE_MODE', !appStatus.MAINTENANCE_MODE)"
                            layout="horizontal"
                            size="small"
                        />
                    </div>
                    <div class="item">
                        <form-toggle
                            label="Filler Maintenance Mode"
                            :checked="appStatus.FILLER_MAINTENANCE_MODE"
                            @change="updateStatus('FILLER_MAINTENANCE_MODE', !appStatus.FILLER_MAINTENANCE_MODE)"
                            layout="horizontal"
                            size="small"
                        />
                    </div>
                </div>
            </div>
        </elem-dropdown>

        <div v-if="developerError" class="warning" id="dev-error" @mouseenter="showDevError" @mouseleave="hideTooltip">
            <i class="far fa-exclamation-triangle"></i>
        </div>
        <div
            v-if="endpointChanged"
            class="warning"
            id="endpoint-notice"
            @mouseenter="showEndpointNotice"
            @mouseleave="hideTooltip"
        >
            <i class="far fa-server"></i>
        </div>
    </div>
</template>

<script>
    const dateFormat = require('dateformat');
    import { mapGetters } from 'vuex';
    import FormToggle from './FormToggle.vue';

    export default {
        name: 'DevTools',
        data() {
            return {
                apiEndpoint: '',
                useQAStatusEndpoint: false,
            };
        },
        components: { FormToggle },
        computed: {
            ...mapGetters({
                developerError: 'base/developerError',
                appStatus: 'base/appStatus',
                appLoadedAt: 'base/appLoadedAt',
            }),
            endpointChanged() {
                return this.apiEndpoint !== this.$env.API_ENDPOINT;
            },
            appExpireTime() {
                return this.appStatus.APP_EXPIRE ? dateFormat(parseInt(this.appStatus.APP_EXPIRE), 'HH:MM:ss') : '-';
            },
            appLoadedTime() {
                return dateFormat(this.appLoadedAt, 'HH:MM:ss');
            },
        },
        methods: {
            showDevError() {
                this.$store.commit('tooltip/set', {
                    message: this.developerError,
                    trigger: '#dev-error',
                    class: 'error',
                    position: 'right',
                });
            },
            showEndpointNotice() {
                this.$store.commit('tooltip/set', {
                    message: `The API server URL has been manually overridden`,
                    trigger: '#endpoint-notice',
                    position: 'right',
                });
            },
            showAppStatusInfo() {
                this.$store.dispatch('dialog/set', {
                    type: 'info',
                    header: 'Change App Status',
                    body: `In development environment, toggle 'Use QA Status URL' to enable testing using the default QA APP_STATUS_URL. Note that changes made here to app expiration/toggling maintenance modes will actually change the 'status-qa.json' file on S3.`,
                });
            },
            toggleUseQAStatusEndpoint() {
                this.useQAStatusEndpoint = !this.useQAStatusEndpoint;
                localStorage.useQAStatusEndpoint = this.useQAStatusEndpoint ? 1 : 0;
            },
            hideTooltip() {
                this.$store.commit('tooltip/clear');
            },
            updateApiEndpoint(value) {
                value = value.trim();
                this.apiEndpoint = value;
                value = value.replace(/\/$/, '');
                this.$api.url = value;
                if (value === this.$env.API_ENDPOINT) {
                    localStorage.removeItem('apiEndpoint');
                } else {
                    localStorage.apiEndpoint = value;
                }
            },
            resetApiEndpoint() {
                localStorage.removeItem('apiEndpoint');
                this.apiEndpoint = this.$api.url = this.$env.API_ENDPOINT;
            },
            async updateStatus(key, value) {
                await this.$api.post('/updateQaStatus', { key, value });
                await this.$store.dispatch('base/updateAppStatus', true);
            },
        },
        created: function () {
            if (localStorage.apiEndpoint) {
                this.apiEndpoint = localStorage.apiEndpoint;
            } else if (this.$api.url) {
                this.apiEndpoint = this.$api.url;
            } else {
                this.$env.onLoad((env) => {
                    this.apiEndpoint = env.API_ENDPOINT;
                });
            }

            if (parseInt(localStorage.useQAStatusEndpoint)) this.useQAStatusEndpoint = true;
        },
    };
</script>

<style lang="scss" scoped>
    .dev-tools-wrapper {
        position: fixed;
        bottom: 10px;
        left: 10px;
        z-index: 9999;
        display: flex;
        align-items: center;

        > * {
            margin-right: 10px;
        }
    }

    #dev-tools {
        @include circle(50px);
        opacity: 0.6;
        border: 1px solid $color-border;
        background-color: $color-white;
        cursor: pointer;
        transition: background-color 0.2s;

        &:hover {
            background-color: $color-border;
        }

        @media print {
            display: none;
        }
    }

    .dropdown {
        width: 300px;
        padding: 5px 10px;

        h5 {
            margin: 0;
            border-bottom: 1px solid $color-border;
        }

        .header {
            font-weight: $bold;
            margin: 4px 0;
        }

        > a,
        .item {
            padding: 4px 0 4px 10px;
        }

        > a {
            display: block;
            cursor: pointer;

            &:hover {
                background-color: $color-disabled-bg;
            }
        }

        .item {
            display: flex;
            align-items: center;

            &.short {
                padding-top: 0;
                padding-bottom: 0;
            }

            ::v-deep input:not(:hover) {
                border-color: transparent;
            }
        }

        .disabled .item:not(.override) {
            opacity: 0.5;
            pointer-events: none;
        }

        .end-button {
            @include border-radius-small;
            padding: 6px 8px;
            margin-left: 3px;

            &:hover {
                background-color: $color-disabled-bg;
            }

            i {
                margin: 0;
            }
        }
    }

    .app-status {
        display: inline-block;
    }

    .refresh {
        margin-right: 1em;
    }

    .time {
        text-align: center;
    }

    ::v-deep .control-wrapper.toggle {
        width: 100%;
        padding: 0 !important;
    }

    .warning {
        font-size: 44px;
    }

    #dev-error {
        color: $color-error;
    }
</style>